import { getAcpAppName } from "@libs/acp/utils/getAcpAppName";
import {
	clearACLocalData,
	handleRefreshToken,
	shouldRefreshToken,
} from "@libs/acp/utils/handleRefreshToken";
import { hasValidSession } from "@libs/acp/utils/handleToken";
import { removeCookie } from "@libs/utils/helpers/getSafeCookies";
import safestorage from "@libs/utils/helpers/getSafeStorage";
import { isDev } from "@libs/utils/helpers/isDev";

import { useUserStore } from "@libs/acp/store/user/useUserStore";
import constants from "@libs/acp/common/constants";

import type { SessionProps } from "@libs/acp/utils/types";

const useAuthSession = () => {
	const { resetACP, updateACPAuthSession } = useUserStore();

	const appName = getAcpAppName();
	const isCustomBuild = !!import.meta.env.VITE_CUSTOM_BUILD;

	const storageKey =
		appName === "cp"
			? constants.cpAuth.AUTH_USER_TOKEN
			: constants.acAuth.AUTH_USER_TOKEN;

	const subjectCookie = isDev()
		? `${constants.legacyAuth.SUBJECT}_DEV`
		: constants.legacyAuth.SUBJECT;

	const isAuthenticated = (): boolean => {
		const auth = useUserStore.getState().authenticatedUser?.acp?.auth;
		if (!auth?.access_token) return false;
		return hasValidSession({
			access_token: auth.access_token,
			refresh_token: auth.refresh_token,
		});
	};

	const logout = () => {
		resetACP();
		removeCookie(subjectCookie);
		removeCookie("user_logged_in");
		removeCookie(constants.cookies.MFA_POSITIVE_DATA_PASSED);
	};

	const getStoredSession = async (): Promise<SessionProps | null> => {
		const fromStorage = (await safestorage.getItem(storageKey)) as SessionProps | null;

		if (isCustomBuild) {
			const storeAuth = useUserStore.getState().authenticatedUser?.acp?.auth;
			const storeHasBothTokens =
				storeAuth?.access_token && storeAuth?.refresh_token;

			if (storeHasBothTokens) {
				return {
					access_token: storeAuth.access_token,
					refresh_token: storeAuth.refresh_token,
				};
			}
		}

		return fromStorage;
	};

	const checkSession = async (): Promise<void> => {
		const [needsRefresh, session] = await Promise.all([
			shouldRefreshToken(),
			getStoredSession(),
		]);

		if (!needsRefresh) return;

		if (!session?.refresh_token) {
			logout();
			await clearACLocalData();
			return;
		}

		try {
			const newSession = await handleRefreshToken({
				refreshToken: session.refresh_token,
			});

			if (!newSession?.access_token) {
				logout();
				await clearACLocalData();
				return;
			}

			updateACPAuthSession({
				access_token: newSession.access_token,
				refresh_token: newSession.refresh_token,
				isAuthenticated: true,
			});
		} catch (err) {
			console.error("[useAuthSession] checkSession error:", err);
			logout();
			await clearACLocalData();
		}
	};

	const getUpdatedSession = async (): Promise<SessionProps | null> => {
		const needsRefresh = await shouldRefreshToken();

		const session = await getStoredSession();

		if (!needsRefresh) {
			return session;
		}

		if (!session?.refresh_token || !session?.access_token) {
			logout();
			return null;
		}

		try {
			const newSession = await handleRefreshToken({
				refreshToken: session.refresh_token,
			});

			if (!newSession?.access_token) {
				logout();
				return null;
			}

			updateACPAuthSession({
				access_token: newSession.access_token,
				refresh_token: newSession.refresh_token,
				isAuthenticated: true,
			});

			return {
				access_token: newSession.access_token,
				refresh_token: newSession.refresh_token,
			};
		} catch (err) {
			console.error("[useAuthSession] getUpdatedSession error:", err);
			logout();
			return null;
		}
	};

	return {
		isAuthenticated,
		logout,
		checkSession,
		getUpdatedSession,
	};
};

export default useAuthSession;
